import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Download,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  X,
  Bell,
  BellOff,
  Users,
  FileCheck,
  ChevronDown,
  ChevronRight,
  Star,
  Layers,
  Database,
  History,
  ArrowRight,
} from "lucide-react";
import { api } from "@/api/client";
import apiClient from "@/api/client";
import { useRBAC } from "@/hooks/useRBAC";
import { useAuthStore } from "@/store/authSlice";
import ConfirmModal from "@/components/common/ConfirmModal";
// import KnowledgeContextPanel from "@/components/knowledge/KnowledgeContextPanel"; // hidden for now
import FragmentTree from "@/components/documents/FragmentTree";
// import ClassificationSuggestionPanel from "@/components/documents/ClassificationSuggestionPanel"; // hidden for now
import { useConfirmModal } from "@/hooks/useConfirmModal";
import {
  getDocumentFragments,
  getDocumentChunks,
  type DocumentChunksResponse,
  type DocumentFragmentNode,
} from "@/api/knowledge";
import type {
  Document,
  DocumentVersion,
  ApprovalHistoryItem,
} from "@/types/document";
import {
  CONFIDENTIALITY_COLORS,
  CONFIDENTIALITY_LABELS,
  STATUS_COLORS,
  AKSI_LABELS,
  AKSI_COLORS,
} from "@/types/document";

interface DocumentChangeItem {
  id: string;
  batch_id: string;
  field: string;
  label: string;
  old_value: string | null;
  new_value: string | null;
  username: string | null;
  full_name: string | null;
  created_at: string;
}

// Group change rows by batch_id (one save event) preserving newest-first order.
function groupChanges(rows: DocumentChangeItem[]) {
  const order: string[] = [];
  const map = new Map<string, DocumentChangeItem[]>();
  for (const r of rows) {
    if (!map.has(r.batch_id)) {
      map.set(r.batch_id, []);
      order.push(r.batch_id);
    }
    map.get(r.batch_id)!.push(r);
  }
  return order.map((b) => ({ batch_id: b, items: map.get(b)! }));
}

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canReview, canApprove, canPublish, canEditDocument, canDelete, isAdmin, isAuditor } =
    useRBAC();
  const currentUser = useAuthStore((s) => s.user);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string>("");
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(true);
  const [strukturOpen, setStrukturOpen] = useState(false);
  const [chunksOpen, setChunksOpen] = useState(false);
  const [chunkPage, setChunkPage] = useState(1);
  const { modalProps, confirm } = useConfirmModal();

  const { data: fragments } = useQuery<DocumentFragmentNode[]>({
    queryKey: ["document-fragments", id],
    queryFn: () => getDocumentFragments(id!),
    enabled: !!id && strukturOpen,
  });

  const canSeeChunks = isAdmin || isAuditor;
  const { data: chunks } = useQuery<DocumentChunksResponse>({
    queryKey: ["document-chunks", id, chunkPage],
    queryFn: () => getDocumentChunks(id!, chunkPage),
    enabled: !!id && chunksOpen && canSeeChunks,
  });

  const handleDownload = async () => {
    const res = await apiClient.get(`/documents/${id}/download`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = doc?.file_name || "document";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePreview = async () => {
    const res = await apiClient.get(`/documents/${id}/preview`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    setPreviewUrl(window.URL.createObjectURL(blob));
    setPreviewType(res.headers["content-type"] || "");
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewType("");
  };

  const { data: doc, isLoading } = useQuery<Document>({
    queryKey: ["document", id],
    queryFn: () => api.get(`/documents/${id}`),
    enabled: !!id,
  });

  const { data: versions = [] } = useQuery<DocumentVersion[]>({
    queryKey: ["document-versions", id],
    queryFn: () => api.get(`/documents/${id}/versions`),
    enabled: !!id,
  });

  const { data: approvalHistory = [] } = useQuery<ApprovalHistoryItem[]>({
    queryKey: ["approval-history", id],
    queryFn: () => api.get(`/documents/${id}/approval-history`),
    enabled: !!id,
  });

  const { data: changeHistory = [] } = useQuery<DocumentChangeItem[]>({
    queryKey: ["document-changes", id],
    queryFn: () => api.get(`/documents/${id}/changes`),
    enabled: !!id,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["document", id] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["approval-history", id] });
  };

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/documents/${id}/submit`),
    onSuccess: invalidateAll,
  });

  const reviewMutation = useMutation({
    mutationFn: (params: { action: "APPROVE" | "REJECT"; catatan?: string }) =>
      api.post(`/documents/${id}/review`, params),
    onSuccess: invalidateAll,
  });

  const approveMutation = useMutation({
    mutationFn: (params: { action: "APPROVE" | "REJECT"; catatan?: string }) =>
      api.post(`/documents/${id}/approve`, params),
    onSuccess: invalidateAll,
  });

  const publishMutation = useMutation({
    mutationFn: () => api.post(`/documents/${id}/publish`),
    onSuccess: invalidateAll,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/documents/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      navigate("/documents");
    },
  });

  const watchMutation = useMutation({
    mutationFn: () =>
      doc?.is_watched
        ? api.delete(`/documents/${id}/watch`)
        : api.post(`/documents/${id}/watch`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document", id] });
    },
  });

  if (isLoading || !doc) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const isCreator = currentUser?.user_id === doc.created_by_user_id;
  const canEdit = canEditDocument(doc.created_by_user_id);
  const uniqueTags = [...new Map(doc.tags.map((t) => [t.id, t])).values()];

  return (
    <div className="space-y-5">
      {/* Back + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Link
          to="/documents"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Kembali ke Daftar
        </Link>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => watchMutation.mutate()}
            disabled={watchMutation.isPending}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 ${
              doc.is_watched
                ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            {doc.is_watched ? (
              <BellOff className="h-4 w-4" />
            ) : (
              <Bell className="h-4 w-4" />
            )}
            {doc.is_watched ? "Berhenti Pantau" : "Pantau"}
          </button>

          {/* STEP 1: Submit — Uploader submits DRAFT/DITOLAK for review */}
          {/* DITOLAK: only the creator can resubmit/edit; DRAFT: normal canEdit */}
          {((doc.status_kode === "DRAFT" && canEdit) ||
            (doc.status_kode === "DITOLAK" && isCreator)) && (
            <button
              onClick={() =>
                confirm({
                  title: "Ajukan Tinjauan",
                  message: "Dokumen akan diajukan untuk ditinjau. Lanjutkan?",
                  confirmLabel: "Ajukan",
                  variant: "info",
                  onConfirm: () => submitMutation.mutate(),
                })
              }
              disabled={submitMutation.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              Ajukan Tinjauan
            </button>
          )}
          {((doc.status_kode === "DRAFT" && canEdit) ||
            (doc.status_kode === "DITOLAK" && isCreator)) && (
            <Link
              to={`/documents/${id}/edit`}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Link>
          )}
          {/* Dokumen sudah disetujui/diterbitkan: ganti file → tinjau ulang (kembali DRAFT). */}
          {canEdit &&
            (doc.status_kode === "DISETUJUI" || doc.status_kode === "DITERBITKAN") && (
              <Link
                to={`/documents/${id}/edit`}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-100"
              >
                <Edit className="h-4 w-4" />
                Ganti File / Tinjau Ulang
              </Link>
            )}
          {/* STEP 2: Review (Level 1) — Manager reviews DIAJUKAN */}
          {canReview && doc.status_kode === "DIAJUKAN" && (
            <>
              <button
                onClick={() =>
                  confirm({
                    title: "Loloskan Tinjauan",
                    message:
                      "Dokumen akan diloloskan dari tahap tinjauan. Lanjutkan?",
                    confirmLabel: "Loloskan",
                    variant: "success",
                    onConfirm: () =>
                      reviewMutation.mutate({ action: "APPROVE" }),
                  })
                }
                disabled={reviewMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Loloskan Tinjauan
              </button>
              <button
                onClick={() =>
                  confirm({
                    title: "Tolak Dokumen",
                    message:
                      "Dokumen akan ditolak dan dikembalikan ke pengaju.",
                    confirmLabel: "Tolak",
                    variant: "danger",
                    withNote: true,
                    notePlaceholder: "Alasan penolakan...",
                    onConfirm: (note) =>
                      reviewMutation.mutate({
                        action: "REJECT",
                        catatan: note,
                      }),
                  })
                }
                disabled={reviewMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Tolak
              </button>
            </>
          )}
          {/* STEP 3: Approve (Level 2) — Leader approves DITINJAU */}
          {canApprove && doc.status_kode === "DITINJAU" && (
            <>
              <button
                onClick={() =>
                  confirm({
                    title: "Setujui Dokumen",
                    message: "Dokumen akan disetujui. Lanjutkan?",
                    confirmLabel: "Setujui",
                    variant: "success",
                    onConfirm: () =>
                      approveMutation.mutate({ action: "APPROVE" }),
                  })
                }
                disabled={approveMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Setujui
              </button>
              <button
                onClick={() =>
                  confirm({
                    title: "Tolak Dokumen",
                    message:
                      "Dokumen akan ditolak dan dikembalikan ke pengaju.",
                    confirmLabel: "Tolak",
                    variant: "danger",
                    withNote: true,
                    notePlaceholder: "Alasan penolakan...",
                    onConfirm: (note) =>
                      approveMutation.mutate({
                        action: "REJECT",
                        catatan: note,
                      }),
                  })
                }
                disabled={approveMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Tolak
              </button>
            </>
          )}
          {/* STEP 4: Publish — Leader publishes DISETUJUI */}
          {canPublish && doc.status_kode === "DISETUJUI" && (
            <button
              onClick={() =>
                confirm({
                  title: "Terbitkan Dokumen",
                  message:
                    "Dokumen akan diterbitkan dan dapat diakses publik. Lanjutkan?",
                  confirmLabel: "Terbitkan",
                  variant: "success",
                  onConfirm: () => publishMutation.mutate(),
                })
              }
              disabled={publishMutation.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              Terbitkan
            </button>
          )}
          {canDelete && (
            <button
              onClick={() =>
                confirm({
                  title: "Hapus Dokumen",
                  message:
                    "Dokumen akan dihapus secara permanen. Tindakan ini tidak dapat dibatalkan.",
                  confirmLabel: "Hapus",
                  variant: "danger",
                  onConfirm: () => deleteMutation.mutate(),
                })
              }
              className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Hapus
            </button>
          )}
        </div>
      </div>

      {/* Main card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-100 px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                {doc.judul}
              </h1>
              {doc.nomor_dokumen && (
                <p className="text-sm text-gray-500 mt-0.5">
                  No: {doc.nomor_dokumen}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[doc.status_kode]}`}
              >
                {doc.status_nama}
              </span>
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${CONFIDENTIALITY_COLORS[doc.tingkat_kerahasiaan]}`}
              >
                {CONFIDENTIALITY_LABELS[doc.tingkat_kerahasiaan]}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Eye className="h-3.5 w-3.5" />
                {doc.total_views}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Users className="h-3.5 w-3.5" />
                {doc.total_watchers}
              </span>
            </div>
          </div>
          {doc.deskripsi && (
            <p className="mt-3 text-sm text-gray-600">{doc.deskripsi}</p>
          )}
        </div>

        {/* Taksonomi breadcrumb */}
        <div className="border-t border-gray-100 px-4 sm:px-6 py-3 bg-gray-50">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            Taksonomi
          </p>
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="inline-flex items-center gap-1 rounded-md bg-white border border-gray-200 px-2 py-0.5">
              <span className="text-gray-700">
                {doc.taxonomy_node_nama || doc.jenis_pnbp_nama}
              </span>
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
            <span className="inline-flex items-center gap-1 rounded-md bg-white border border-gray-200 px-2 py-0.5">
              <span className="font-mono text-xs text-blue-600 font-semibold">
                {doc.siklus_keuangan_kode}
              </span>
              <span className="text-gray-700">{doc.siklus_keuangan_nama}</span>
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
            <span className="inline-flex items-center gap-1 rounded-md bg-white border border-gray-200 px-2 py-0.5">
              <span className="font-mono text-xs text-blue-600 font-semibold">
                {doc.bentuk_dokumen_kode}
              </span>
              <span className="text-gray-700">{doc.bentuk_dokumen_nama}</span>
            </span>
          </div>

          {doc.taxonomy_breadcrumb && doc.taxonomy_breadcrumb.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Domain PNBP
              </p>
              <div className="flex flex-wrap items-center gap-1.5 text-sm">
                {doc.taxonomy_breadcrumb.map((name, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <Link
                      to={`/master/taxonomy?type=PNBP_DOMAIN&path=${encodeURIComponent(
                        doc.taxonomy_node_path
                          ?.split("/")
                          .slice(0, idx + 1)
                          .join("/") || ""
                      )}`}
                      className="inline-flex items-center rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-800 hover:bg-emerald-100 transition"
                    >
                      {name}
                    </Link>
                    {idx < doc.taxonomy_breadcrumb!.length - 1 && (
                      <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {doc.taxonomy_nodes && doc.taxonomy_nodes.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Pohon Taxonomy PNBP
              </p>
              <div className="flex flex-wrap gap-2">
                {doc.taxonomy_nodes.map((node) => (
                  <span
                    key={node.id}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
                      node.is_primary
                        ? "border-amber-300 bg-amber-50 text-amber-900"
                        : "border-blue-200 bg-blue-50 text-blue-800"
                    }`}
                  >
                    {node.is_primary && (
                      <Star className="h-3.5 w-3.5 text-amber-500" fill="currentColor" />
                    )}
                    <span>{node.label}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Metadata grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 px-4 sm:px-6 py-4 sm:py-5 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Klasifikasi PNBP
            </p>
            <p className="text-gray-800">
              {doc.taxonomy_node_nama || doc.jenis_pnbp_nama}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Siklus Keuangan
            </p>
            <p className="text-gray-800">{doc.siklus_keuangan_nama}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Bentuk Dokumen
            </p>
            <p className="text-gray-800 flex items-center gap-2">
              {doc.bentuk_dokumen_nama}
              {doc.bentuk_dokumen_jenis_repositori && (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    doc.bentuk_dokumen_jenis_repositori === "LEGAL"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {doc.bentuk_dokumen_jenis_repositori === "LEGAL" ? "Legal" : "Non-Legal"}
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Dibuat Oleh
            </p>
            <p className="text-gray-800">{doc.created_by_username}</p>
          </div>
          {doc.tanggal_dokumen && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Tanggal Dokumen
              </p>
              <p className="text-gray-800">
                {new Date(doc.tanggal_dokumen).toLocaleDateString("id-ID")}
              </p>
            </div>
          )}
          {(doc.tanggal_berlaku || doc.tanggal_berakhir) && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Masa Berlaku
              </p>
              <p className="text-gray-800">
                {doc.tanggal_berlaku
                  ? new Date(doc.tanggal_berlaku).toLocaleDateString("id-ID")
                  : "—"}
                {" – "}
                {doc.tanggal_berakhir
                  ? new Date(doc.tanggal_berakhir).toLocaleDateString("id-ID")
                  : "—"}
              </p>
            </div>
          )}
        </div>

        {/* Dimensi */}
        {doc.dimensi.length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Dimensi Fungsional
            </p>
            <div className="flex flex-wrap gap-2">
              {doc.dimensi.map((d) => (
                <span
                  key={d.id}
                  className="rounded-full bg-indigo-50 px-3 py-1 text-xs text-indigo-700 font-medium"
                >
                  {d.kode} — {d.nama}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Unit Kerja */}
        {doc.unit_kerja?.length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Unit Kerja
            </p>
            <div className="flex flex-wrap gap-2">
              {doc.unit_kerja.map((u) => (
                <span
                  key={u.id}
                  className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700 font-medium"
                >
                  <span className="font-mono text-[10px] text-amber-500">{u.kode}</span>{" "}
                  {u.nama}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* PIC */}
        {doc.pic?.length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              PIC (Person in Charge)
            </p>
            <div className="flex flex-wrap gap-2">
              {doc.pic.map((p) => (
                <span
                  key={p.id}
                  className="rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700 font-medium"
                >
                  {p.nama}
                  {p.jabatan && (
                    <span className="text-emerald-500"> — {p.jabatan}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stakeholder */}
        {doc.stakeholders?.length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Stakeholder
            </p>
            <div className="flex flex-wrap gap-2">
              {doc.stakeholders.map((s) => (
                <span
                  key={s.id}
                  className="rounded-full bg-purple-50 px-3 py-1 text-xs text-purple-700 font-medium"
                >
                  {s.nama}
                  {s.tipe && (
                    <span className="text-purple-400"> ({s.tipe})</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tags Terstruktur */}
        {uniqueTags.filter((t) => t.kategori !== "custom").length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Tag Terstruktur
            </p>
            <div className="flex flex-wrap gap-2">
              {uniqueTags
                .filter((t) => t.kategori !== "custom")
                .map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700 font-medium"
                  >
                    {tag.nama}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Tags Bebas */}
        {uniqueTags.filter((t) => t.kategori === "custom").length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Tag Bebas
            </p>
            <div className="flex flex-wrap gap-2">
              {uniqueTags
                .filter((t) => t.kategori === "custom")
                .map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-full bg-sky-50 px-3 py-1 text-xs text-sky-700 font-medium"
                  >
                    #{tag.nama}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* File info */}
        {doc.file_name && (
          <div className="border-t border-gray-100 px-4 sm:px-6 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              File Terlampir
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 break-all">
                  {doc.file_name}
                </p>
                {doc.file_size && (
                  <p className="text-xs text-gray-500">
                    {(doc.file_size / 1024 / 1024).toFixed(2)} MB •{" "}
                    {doc.mime_type}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={handlePreview}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Knowledge Context Panel — hidden for now (uncomment to restore) */}
      {/* {id && <KnowledgeContextPanel documentId={id} />} */}

      {/* Version history — collapsible */}
      {versions.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            onClick={() => setVersionsOpen(!versionsOpen)}
            className="flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              Riwayat Versi ({versions.length})
            </h2>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                versionsOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {versionsOpen && (
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3"
                >
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-mono font-semibold text-gray-600">
                    v{v.versi_ke}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">
                      {v.file_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(v.file_size / 1024 / 1024).toFixed(2)} MB • Diunggah{" "}
                      {new Date(v.uploaded_at).toLocaleDateString("id-ID")} oleh{" "}
                      {v.uploaded_by_username}
                    </p>
                    {v.catatan && (
                      <p className="text-xs text-gray-400 mt-0.5 italic">
                        {v.catatan}
                      </p>
                    )}
                  </div>
                  <span
                    className="text-xs font-mono text-gray-400 truncate max-w-24"
                    title={v.file_hash}
                  >
                    {v.file_hash.slice(0, 8)}...
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Metadata change history — collapsible */}
      {changeHistory.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            onClick={() => setChangesOpen(!changesOpen)}
            className="flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <History className="h-4 w-4 text-gray-400" />
              Riwayat Perubahan ({groupChanges(changeHistory).length})
            </h2>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                changesOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {changesOpen && (
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {groupChanges(changeHistory).map((grp) => {
                const first = grp.items[0];
                return (
                  <div key={grp.batch_id} className="px-4 sm:px-6 py-3">
                    <p className="text-xs text-gray-500 mb-2">
                      {new Date(first.created_at).toLocaleString("id-ID")} • oleh{" "}
                      <span className="font-medium text-gray-700">
                        {first.full_name || first.username || "—"}
                      </span>{" "}
                      • {grp.items.length} perubahan
                    </p>
                    <div className="space-y-1.5">
                      {grp.items.map((c) => (
                        <div
                          key={c.id}
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
                        >
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                            {c.label}
                          </span>
                          <span className="text-gray-400 line-through max-w-[20rem] truncate" title={c.old_value || ""}>
                            {c.old_value || "(kosong)"}
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                          <span className="text-gray-800 max-w-[20rem] truncate" title={c.new_value || ""}>
                            {c.new_value || "(kosong)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AI Classification Suggestion — hidden for now (uncomment to restore) */}
      {/* {id && <ClassificationSuggestionPanel docId={id} readOnly={!canEdit} />} */}

      {/* Struktur Dokumen — collapsible (fragments hierarchy) */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <button
          onClick={() => setStrukturOpen(!strukturOpen)}
          className="flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-400" />
            Struktur Dokumen
            <span className="text-sm font-normal text-gray-500">
              (BAB / Pasal / Ayat)
            </span>
          </h2>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform ${
              strukturOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {strukturOpen && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-100">
            <FragmentTree fragments={fragments ?? []} />
          </div>
        )}
      </div>

      {/* Chunks RAG section — HIDDEN for now. Uncomment the block below to restore. */}
      {/* {canSeeChunks && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            onClick={() => setChunksOpen(!chunksOpen)}
            className="flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Database className="h-4 w-4 text-gray-400" />
              Chunks RAG
              <span className="text-sm font-normal text-gray-500">
                {chunks ? `(${chunks.total})` : "(debug retrieval)"}
              </span>
            </h2>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                chunksOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {chunksOpen && (
            <div className="border-t border-gray-100">
              <div className="px-4 sm:px-6 py-3 text-xs text-gray-500 bg-amber-50 border-b border-amber-100">
                Internal RAG state. Chunks adalah potongan teks yang di-embed untuk
                semantic search; berguna untuk debug kualitas retrieval, bukan
                representasi resmi konten dokumen.
              </div>
              {chunks && chunks.items.length === 0 && (
                <div className="px-4 sm:px-6 py-8 text-center text-sm text-gray-400">
                  Belum ada chunk. Trigger reprocess dari panel AI untuk
                  menghasilkan chunks + embedding.
                </div>
              )}
              {chunks && chunks.items.length > 0 && (
                <>
                  <div className="divide-y divide-gray-100">
                    {chunks.items.map((c) => (
                      <div key={c.id} className="px-4 sm:px-6 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-gray-500">
                            #{c.chunk_index}
                          </span>
                          <span className="text-xs text-gray-400">
                            {c.token_count} token
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                          {c.chunk_text.length > 400
                            ? c.chunk_text.slice(0, 400) + "…"
                            : c.chunk_text}
                        </p>
                      </div>
                    ))}
                  </div>
                  {chunks.total_pages > 1 && (
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-100 text-sm">
                      <button
                        type="button"
                        onClick={() => setChunkPage((p) => Math.max(1, p - 1))}
                        disabled={chunkPage <= 1}
                        className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-40 hover:bg-gray-50"
                      >
                        Sebelumnya
                      </button>
                      <span className="text-gray-500">
                        Halaman {chunks.page} dari {chunks.total_pages}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setChunkPage((p) =>
                            Math.min(chunks.total_pages, p + 1)
                          )
                        }
                        disabled={chunkPage >= chunks.total_pages}
                        className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-40 hover:bg-gray-50"
                      >
                        Berikutnya
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )} */}

      {/* Approval history — collapsible */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <button
          onClick={() => setApprovalOpen(!approvalOpen)}
          className="flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-gray-400" />
            Riwayat Persetujuan
            {approvalHistory.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({approvalHistory.length})
              </span>
            )}
          </h2>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform ${
              approvalOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {approvalOpen && approvalHistory.length === 0 && (
          <div className="px-4 sm:px-6 py-8 text-center text-sm text-gray-400 border-t border-gray-100">
            Belum ada riwayat persetujuan untuk dokumen ini.
          </div>
        )}
        {approvalOpen && approvalHistory.length > 0 && (
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {approvalHistory.map((h, index) => (
              <div
                key={h.id}
                className="flex items-start gap-3 px-4 sm:px-6 py-3.5"
              >
                {/* Timeline dot + line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full ${
                      h.aksi.includes("REJECT")
                        ? "bg-red-100"
                        : h.aksi === "PUBLISH"
                          ? "bg-green-100"
                          : "bg-blue-100"
                    }`}
                  >
                    {h.aksi.includes("REJECT") ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : h.aksi === "PUBLISH" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  {index < approvalHistory.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200 mt-1" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-1">
                  <p
                    className={`text-sm font-semibold ${
                      AKSI_COLORS[h.aksi] || "text-gray-700"
                    }`}
                  >
                    {AKSI_LABELS[h.aksi] || h.aksi}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    oleh{" "}
                    <span className="font-medium text-gray-700">
                      {h.full_name || h.username}
                    </span>
                    {" — "}
                    {new Date(h.created_at).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        STATUS_COLORS[
                          h.status_asal as keyof typeof STATUS_COLORS
                        ] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {h.status_asal}
                    </span>
                    <span className="text-xs text-gray-400">→</span>
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        STATUS_COLORS[
                          h.status_tujuan as keyof typeof STATUS_COLORS
                        ] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {h.status_tujuan}
                    </span>
                  </div>
                  {h.catatan && (
                    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-600 italic">
                        "{h.catatan}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="relative bg-white rounded-xl shadow-2xl w-[90vw] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">
                Preview — {doc.file_name}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={closePreview}
                  className="rounded-lg p-1.5 hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-1">
              {previewType.includes("pdf") ? (
                <iframe src={previewUrl} className="w-full h-full rounded" />
              ) : previewType.includes("image") ? (
                <img
                  src={previewUrl}
                  alt={doc.file_name}
                  className="max-w-full max-h-full mx-auto object-contain"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>
                    Preview tidak tersedia untuk tipe file ini. Silakan
                    download.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal {...modalProps} />
    </div>
  );
}
